<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import {
  ANALYTICS_CONSTS as AC,
  ANALYTIC_MODULE_CONSTS as AMC,
  FULLFLIGHT_CONSTS as FFC,
  ACMF_REPORT_DETAILS_CONSTS as ARVC,
  USER_CONSTS as UC,
} from '@/stores/consts'
import {
  AnalyticComparisonRiskScore,
  FlightParameterComparison,
  AnalyticTrendsRiskScore,
  FlightParameterTrends,
  ConditionMonitoringReportDataTrends,
} from '@/utilities/plottingApi'
import {
  ANALYTIC_TRENDS_RISK_SCORE_PLOT_TYPE_ID,
  FLIGHT_PARAMETER_COMPARISON_PLOT_TYPE_ID,
  ANALYTIC_COMPARISON_RISK_SCORE_PLOT_TYPE_ID,
  FLIGHT_PARAMETER_TRENDS_PLOT_TYPE_ID,
  CONDITION_MONITORING_REPORT_DATA_TRENDS_PLOT_TYPE_ID,
  REPORT_PARAMETER_TRENDS_PLOT_TYPE_ID,
} from '@/utilities/appConsts'

export default {
  computed: {
    ...mapState(AC.NAMESPACE, [
      'quartileSplitResults',
      'quartileSplitResultsMissingTails',
    ]),
    ...mapState(AMC.NAMESPACE, [
      'amcAnalyticTrendsDataResults',
      'amcAnalyticComparisonDataResults',
      'amcAnalyticComparisonDataResultsMissingTails',
    ]),
    ...mapState(ARVC.NAMESPACE, [
      'parameterGraph',
      'reportPlotGraphGroups',
      'reportPlotGraph',
    ]),
    ...mapGetters(FFC.NAMESPACE, ['filteredFullFlightData']),
    ...mapGetters(AMC.NAMESPACE, ['getPagePlotFeatures']),
    ...mapGetters(UC.NAMESPACE, [
      'flightDataAnalyticComparisonPermission',
      'flightDataFlightComparisonPermission',
      'flightDataAnalyticTrendsPermission',
      'flightDataFlightTrendsPermission',
      'flightDataAcmfTrendsPermission',
    ]),
  },

  methods: {
    ...mapActions(AC.NAMESPACE, [AC.GET_QUARTILE_RESULTS]),
    ...mapActions(AMC.NAMESPACE, [
      AMC.GET_AMC_ANALYTIC_TRENDS_DATA,
      AMC.GET_AMC_ANALYTIC_COMPARISON_DATA,
      AMC.SET_AMC_PAGE_PLOT_FEATURES,
    ]),
    ...mapActions(FFC.NAMESPACE, [FFC.GET_COMPLETE_FULL_FLIGHT_DATA]),
    ...mapActions(ARVC.NAMESPACE, [ARVC.GET_PARAMETER_GRAPH]),
    ...mapMutations(['updateApiCallStatus']),

    async handleLoadData(plotId, plotTypeId, tabIds) {
      const isMxRecPlot = !!(
        this.$route &&
        this.$route.name &&
        this.$route.name === 'Mx Recs'
      )
      this.updateApiCallStatus('reset')

      if ((tabIds || []).length > 0) {
        const tabs = Array.isArray(tabIds) ? tabIds : [tabIds]
        const tabId = tabs.pop()

        const initData = this.initPlotChartData(plotId, plotTypeId, tabId)
        if (!initData.isFound) return

        this.loadingPlotIds.push(plotId)
        const items = initData.items
        const tabIndex = initData.tab.tabIndex
        const tabHidden = initData.tab.hidden

        const baseProps = {
          chartIndex: 0,
          iconColor: initData.iconColor,
          missingTails: [],
          plotId,
          plotTypeId,
          rawData: [],
          tabHidden,
          tabId,
          tabIndex,
        }

        let selector = ''
        const pagePlot = this.getPagePlotGivenPlotId(plotId)
        const isInvalidPlot = pagePlot.isInvalid || false
        if (isMxRecPlot) {
          // Intially set the isAnyPrivateFeatureVisible and hasAnyPublicFeaturesVisible to false
          pagePlot.isAnyPrivateFeatureVisible = false
          pagePlot.hasAnyPublicFeaturesVisible = false
        }

        if (
          plotTypeId === ANALYTIC_TRENDS_RISK_SCORE_PLOT_TYPE_ID &&
          this.flightDataAnalyticTrendsPermission
        ) {
          const aft = new AnalyticTrendsRiskScore(initData.tab)
          const selector = aft.getSelector()
          const tailName = selector.tailName
          const featureId = selector.featureId
          const featureName = selector.featureName
          const startDate = selector.startDate
          const endDate = selector.endDate
          const isRiskScore = selector.isRiskScore

          await this[AMC.GET_AMC_ANALYTIC_TRENDS_DATA]({
            isInvalidPlot,
            tailName,
            featureId,
            startDate,
            endDate,
            isRiskScore,
          })
          const analyticTrendsData = this.amcAnalyticTrendsDataResults
            .featureData
            ? this.amcAnalyticTrendsDataResults.featureData
            : []

          const sortedData = analyticTrendsData
            .sort(
              (a, b) =>
                new Date(a.timestamp + 'Z').getTime() -
                new Date(b.timestamp + 'Z').getTime(),
            )
            .map(point => {
              return [new Date(point.timestamp + 'Z').getTime(), point.point]
            })
          const rawData = analyticTrendsData.map(item => {
            return {
              flightNumber: item.flightNumber,
              point: item.point,
              timestamp: new Date(item.timestamp + 'Z').getTime(),
              version: item.version,
            }
          })
          const separateCharts = pagePlot.options?.separateCharts || false
          const chartIndex = separateCharts ? baseProps.tabIndex : 0

          // Save the page plot features with tab details to store
          // selector constant also contains the feature data but amcAnalyticTrendsDataResults contains updated ones
          if (this.amcAnalyticTrendsDataResults.id && isMxRecPlot) {
            await this[AMC.SET_AMC_PAGE_PLOT_FEATURES]({
              plotId: plotId,
              featureData: {
                id: this.amcAnalyticTrendsDataResults.id,
                isPrivate: this.amcAnalyticTrendsDataResults.isPrivate,
                tabIndex: initData.tab.tabIndex,
                tabHidden: initData.tab.hidden,
              },
            })

            // Update the isAnyPrivateFeatureVisible by features isPrivate Value
            pagePlot.isAnyPrivateFeatureVisible =
              this.isAnyPrivateFeaturesVisible(plotId)
            pagePlot.hasAnyPublicFeaturesVisible =
              this.hasAnyPublicFeaturesVisible(plotId)
            pagePlot.plotVisible =
              !this.isAnyPrivateFeaturesVisible(plotId) &&
              this.hasAnyPublicFeaturesVisible(plotId)
                ? this.isPlotVisible(pagePlot)
                : false
          }

          items.push({
            ...baseProps,
            chartIndex,
            seriesTitle: `${featureName} - ${tailName}`,
            data: sortedData,
            rawData,
            unit: `${featureName} - ${tailName}`,
            keys: this.amcAnalyticTrendsDataResults.keys || null,
          })
        }

        if (
          plotTypeId === FLIGHT_PARAMETER_COMPARISON_PLOT_TYPE_ID &&
          this.flightDataFlightComparisonPermission
        ) {
          const fpc = new FlightParameterComparison(initData.tab)
          const selector = fpc.getSelector()

          pagePlot.hasAnyPublicFeaturesVisible = true

          await this[AC.GET_QUARTILE_RESULTS](selector)

          items.push({
            ...baseProps,
            chartIndex: 0,
            data: this.quartileSplitResults,
            missingTails: this.quartileSplitResultsMissingTails,
          })
        }

        if (
          plotTypeId === ANALYTIC_COMPARISON_RISK_SCORE_PLOT_TYPE_ID &&
          this.flightDataAnalyticComparisonPermission
        ) {
          const afc = new AnalyticComparisonRiskScore(initData.tab)
          selector = afc.getSelector()
          selector.isInvalidPlot = isInvalidPlot

          await this[AMC.GET_AMC_ANALYTIC_COMPARISON_DATA](selector)

          // Save the page plot features with tab details to store
          // amcAnalyticComparisonDataResults wont have any feature details so fetch it from selector
          if (selector.features.length && isMxRecPlot) {
            // to set the state.pagePlotFeatures for all the selected features
            selector.features.forEach(async item => {
              const privateIdx =
                pagePlot.privateRiskScoreIds &&
                pagePlot.privateRiskScoreIds.length > 0
                  ? pagePlot.privateRiskScoreIds.findIndex(x => x === item.id)
                  : -1
              await this[AMC.SET_AMC_PAGE_PLOT_FEATURES]({
                plotId: plotId,
                featureData: {
                  id: item.id,
                  isPrivate: item.isPrivate || privateIdx > -1,
                  tabIndex: initData.tab.tabIndex,
                  tabHidden: initData.tab.hidden,
                },
              })
            })

            // Update the isAnyPrivateFeatureVisible by feature/Tab isPrivate Value for Analytic Comparison
            pagePlot.isAnyPrivateFeatureVisible =
              this.isAnyPrivateFeaturesVisible(plotId)
            pagePlot.hasAnyPublicFeaturesVisible =
              this.hasAnyPublicFeaturesVisible(plotId)

            pagePlot.plotVisible =
              !this.isAnyPrivateFeaturesVisible(plotId) &&
              this.hasAnyPublicFeaturesVisible(plotId)
                ? this.isPlotVisible(pagePlot)
                : false
          }

          items.push({
            ...baseProps,
            chartIndex: 0,
            data: this.amcAnalyticComparisonDataResults,
            missingTails: this.amcAnalyticComparisonDataResultsMissingTails,
          })
        }

        if (
          plotTypeId === FLIGHT_PARAMETER_TRENDS_PLOT_TYPE_ID &&
          this.flightDataFlightTrendsPermission
        ) {
          const fpt = new FlightParameterTrends(initData.tab)
          const selector = fpt.getSelector()

          await this[FFC.GET_COMPLETE_FULL_FLIGHT_DATA](selector)

          const seriesTitle =
            this.filteredFullFlightData.length > 0
              ? `${this.filteredFullFlightData[0].component} - ${this.filteredFullFlightData[0].parameter} - ${this.filteredFullFlightData[0].aircraft}`
              : ''
          const data =
            this.filteredFullFlightData.length > 0
              ? this.filteredFullFlightData[0].data
              : []
          const rawData =
            this.filteredFullFlightData.length > 0
              ? this.filteredFullFlightData[0].data.map(item => {
                  return {
                    point: item[1],
                    timestamp: item[0],
                  }
                })
              : []
          const flightMappings =
            this.filteredFullFlightData.length > 0
              ? this.filteredFullFlightData[0].flightMappings
              : []
          const unit =
            this.filteredFullFlightData.length > 0
              ? this.filteredFullFlightData[0].unit || 'N/A'
              : ''
          const keys =
            this.filteredFullFlightData.length > 0
              ? this.filteredFullFlightData[0].keys
              : []
          const pagePlot = this.getPagePlotGivenPlotId(plotId)
          const separateCharts = pagePlot.options?.separateCharts || false
          const chartIndex = separateCharts ? baseProps.tabIndex : 0

          pagePlot.hasAnyPublicFeaturesVisible = true

          items.push({
            ...baseProps,
            chartIndex,
            seriesTitle,
            data,
            rawData,
            flightMappings,
            unit,
            keys,
          })
        }

        if (
          plotTypeId === CONDITION_MONITORING_REPORT_DATA_TRENDS_PLOT_TYPE_ID &&
          this.flightDataAcmfTrendsPermission
        ) {
          const cmrdt = new ConditionMonitoringReportDataTrends(initData.tab)
          const selector = cmrdt.getSelector()
          selector.isDataElementId = true
          await this[ARVC.GET_PARAMETER_GRAPH](selector)
          const pagePlot = this.getPagePlotGivenPlotId(plotId)
          const separateCharts = pagePlot.options?.separateCharts || false
          const chartIndex = separateCharts ? baseProps.tabIndex : 0

          items.push({
            ...baseProps,
            chartIndex,
            seriesTitle: this.parameterGraph.parameterName,
            data: this.parameterGraph.values
              ? this.parameterGraph.values.map(item => {
                  return [new Date(item.x + 'Z').getTime(), item.y]
                })
              : [],
            rawData: this.parameterGraph.values
              ? this.parameterGraph.values.map(item => {
                  return {
                    point: item.y,
                    timestamp: new Date(item.x + 'Z').getTime(),
                  }
                })
              : [],
            flightMappings: [],
            unit: this.parameterGraph.thresholds
              ? this.parameterGraph.thresholds[0].units
              : 'N/A',
            keys: null,
          })
        }

        if (plotTypeId === REPORT_PARAMETER_TRENDS_PLOT_TYPE_ID) {
          const pagePlot = this.getPagePlotGivenPlotId(plotId)
          const chartIndex = 0

          let data = {}
          if (this.reportPlotGraphGroups) {
            data = this.reportPlotGraphGroups.graphs.filter(
              item => item.graphName === pagePlot.title,
            )[0].acmfDataElements[tabIndex]
          }

          items.push({
            ...baseProps,
            chartIndex,
            seriesTitle: pagePlot.options.data.tabs[tabIndex].title,
            data: data.point
              ? data.point.map(item => {
                  return [new Date(item.x + 'Z').getTime(), +item.y?.toFixed(2)]
                })
              : [],
            rawData: data.point
              ? data.point.map(item => {
                  return {
                    point: +item.y?.toFixed(2),
                    timestamp: new Date(item.x + 'Z').getTime(),
                  }
                })
              : [],
            flightMappings: [],
            unit: data.measurementUnit ? data.measurementUnit : 'N/A',
            xAxisLabel: 'Date',
          })
        }

        this.chartData = items
          .map(cd => {
            const minMaxValues = this.getHighestandLowestValue(cd.data)
            const override = {
              plotId: cd.plotId,
              min: minMaxValues.min,
              max: minMaxValues.max,
              tabIndex: cd.tabIndex,
              unit: cd.unit,
            }
            const newItem = {
              ...cd,
              override,
            }
            if (cd.plotId === plotId) {
              newItem.dateUpdated = new Date().getTime()
            }
            return newItem
          })
          .sort((a, b) => a.tabIndex - b.tabIndex)

        if (tabs.length === 0) {
          this.loadingPlotIds = this.loadingPlotIds.filter(id => id !== plotId)
          this.applyChartDataGivenOverrides()
        }
        if (tabs.length > 0) {
          await this.handleLoadData(plotId, plotTypeId, tabs)
        }
      }
    },

    isAnyPrivateFeaturesVisible(plotId) {
      // If any of the visible tab feature is isPrivate then return the plot
      // should not be visible to others and hence return true
      const pagePlotFeature = this.getPagePlotFeatures.find(
        plotFeature => plotFeature.plotId === plotId,
      )

      if (
        pagePlotFeature &&
        pagePlotFeature.features &&
        pagePlotFeature.features.length
      ) {
        // if there is feature/Tab which is visible and it has isPrivate set to true then the plot should be not displayed to external users
        const features = pagePlotFeature.features.filter(
          feature => feature.isPrivate && !feature.tabHidden,
        )
        return features.length !== 0
      }

      return false
    },

    hasAnyPublicFeaturesVisible(plotId) {
      // If plot has tabs and any of the visible tab feature is not private
      const pagePlotFeature = this.getPagePlotFeatures.find(
        plotFeature => plotFeature.plotId === plotId,
      )

      if (
        pagePlotFeature &&
        pagePlotFeature.features &&
        pagePlotFeature.features.length
      ) {
        // if there is feature/Tab which is visible and it has isPrivate set to true then the plot should be not displayed to external users
        const features = pagePlotFeature.features.filter(
          feature => !feature.isPrivate && !feature.tabHidden,
        )
        return features.length !== 0
      }

      return false
    },

    minMax2DArray(arr) {
      let max = -Number.MAX_VALUE
      let min = Number.MAX_VALUE
      arr.forEach(e => {
        if (max < e) {
          max = e
        }
        if (min > e) {
          min = e
        }
      })
      return { max: max, min: min }
    },

    getHighestandLowestValue(data) {
      const minAndMax = {
        min: null,
        max: null,
      }
      if (data && data.length) {
        const yAxisData = data.map(item => item.y || item[1])
        const minMaxFromArray = this.minMax2DArray(yAxisData)
        const min = minMaxFromArray.min
        const max = minMaxFromArray.max
        const formattedMin = Number.isInteger(min)
          ? min
          : parseFloat(min.toFixed(2))
        const formattedMax = Number.isInteger(max)
          ? max
          : parseFloat(max.toFixed(2))
        minAndMax.min = formattedMin
        minAndMax.max = formattedMax
      }
      return minAndMax
    },
  },
}
</script>
